import React, { useState } from "react";
import api, { money } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { PageHeader, Spinner } from "@/components/shared";
import { BarChart3, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const REPORTS = [
  { key: "sales_by_consignor", label: "Sales by Consignor" },
  { key: "amount_owed", label: "Amount Owed by Consignor" },
  { key: "sales_by_month", label: "Sales by Month" },
  { key: "payouts_by_month", label: "Payouts by Month" },
  { key: "expiring_inventory", label: "Expiring Inventory" },
  { key: "expired_inventory", label: "Expired Inventory" },
  { key: "revenue_split", label: "Store vs Consignor Revenue" },
];

export default function Reports() {
  const { store } = useStore();
  const [active, setActive] = useState("sales_by_consignor");
  const [data, setData] = useState(null);
  const cur = store?.currency || "USD";

  const load = (key) => { setActive(key); setData(null); api.get(`/reports/${key}`).then((r) => setData(r.data)); };
  React.useEffect(() => { load("sales_by_consignor"); }, [store?.id]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [data.columns, ...data.rows];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `${active}.csv`; a.click();
  };

  const isMoney = active !== "expiring_inventory" && active !== "expired_inventory";
  const chartable = ["sales_by_consignor", "amount_owed", "sales_by_month", "payouts_by_month", "revenue_split"].includes(active);
  const chartData = data && chartable ? data.rows.slice(0, 12).map((r) => ({ name: String(r[0]), value: Number(r[1]) })) : [];
  const colors = ["#047857", "#059669", "#d97706", "#0ea5e9", "#8b5cf6", "#e11d48"];

  return (
    <div>
      <PageHeader title="Reports" subtitle="Understand your consignment business">
        <button data-testid="export-csv-btn" onClick={exportCsv} className="flex items-center gap-2 border border-slate-300 hover:bg-slate-100 px-4 py-2 rounded-lg text-sm font-medium text-slate-700">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-6">
        {REPORTS.map((r) => (
          <button key={r.key} data-testid={`report-tab-${r.key}`} onClick={() => load(r.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${active === r.key ? "bg-emerald-700 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"}`}>
            {r.label}
          </button>
        ))}
      </div>

      {!data ? <Spinner /> : (
        <div className="grid lg:grid-cols-2 gap-6">
          {chartData.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 h-80 min-h-[320px]">
              <ResponsiveContainer width="100%" height="100%" minHeight={280}>
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 40 }}>
                  <XAxis dataKey="name" angle={-30} textAnchor="end" tick={{ fontSize: 11 }} height={60} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => isMoney ? money(v, cur) : v} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>{chartData.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className={`bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden ${chartData.length === 0 ? "lg:col-span-2" : ""}`}>
            <table className="w-full text-sm" data-testid="report-table">
              <thead className="bg-slate-50 border-b border-slate-200"><tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                {data.columns.map((c, i) => <th key={i} className={`px-4 py-3 ${i > 0 ? "text-right" : ""}`}>{c}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100">
                {data.rows.map((row, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    {row.map((c, j) => (
                      <td key={j} className={`px-4 py-2.5 ${j > 0 ? "text-right font-mono-fin" : "font-medium text-slate-800"}`}>
                        {j > 0 && isMoney && typeof c === "number" ? money(c, cur) : c}
                      </td>
                    ))}
                  </tr>
                ))}
                {data.rows.length === 0 && <tr><td colSpan={data.columns.length} className="px-4 py-8 text-center text-slate-400">No data.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
